import { CSSProperties, useEffect, useRef, useState } from 'react';

/**
 * Live view of a headset's display ("meekijken").
 *
 * Opens ws://<pc>:9090/ws/hmd/<serial>/cast on the room PC. The first viewer
 * makes the agent start the cast on the headset (shell >= 1.0.21): ToBService
 * renders the composited screen into a hardware H.264 encoder and the agent
 * relays the Annex B access units untouched. Here they go straight into
 * WebCodecs (Chromium decodes in hardware) and onto a canvas, so the operator
 * sees the fence, PUI dialogs and a dead streaming client exactly as the
 * visitor does, with well under a second of delay.
 *
 * Wire format of a binary message: [flags:1][pts_us:8][Annex B NALs];
 * flags bit0 = keyframe (SPS/PPS included), bit1 = codec config only. Text
 * messages: {"type":"hello"|"stats"|"ended"|"error", ...}. Closing the socket
 * is how a viewer leaves; the agent stops the headset a few seconds after the
 * last one is gone, and the headset stops itself after max_s regardless.
 */

export type HmdCastProfile = 'low' | 'normal' | 'high';

export const HMD_CAST_PROFILES: Array<{ id: HmdCastProfile; label: string; hint: string }> = [
  { id: 'low', label: 'Zuinig', hint: '480 px · 0,8 Mbit/s' },
  { id: 'normal', label: 'Normaal', hint: '640 px · 1,5 Mbit/s' },
  { id: 'high', label: 'Scherp', hint: '960 px · 4 Mbit/s' },
];

interface HeadsetStats {
  fps?: number; kbps?: number; enc_ms?: number; cpu_pct?: number; batt_temp_c?: number; batt_ma?: number;
}

interface Props {
  host: string;
  serial: string;
  profile: HmdCastProfile;
  /** Ends the cast on the agent (POST /api/hmd/{serial}/cast/stop); used when switching profile. */
  stopCast?: () => Promise<unknown>;
  onClose: () => void;
}

const FLAG_KEY = 1, FLAG_CONFIG = 2, HEADER = 9;

/** avc1.PPCCLL from the SPS in a codec-config payload (Annex B). */
function codecStringFromConfig(cfg: Uint8Array): string {
  for (let i = 0; i + 4 < cfg.length && i < 16; i++) {
    if (cfg[i] === 0 && cfg[i + 1] === 0 && cfg[i + 2] === 1) {
      const nalType = cfg[i + 3] & 0x1f;
      if (nalType === 7 && i + 6 < cfg.length) {
        const hex = (b: number) => b.toString(16).padStart(2, '0').toUpperCase();
        return `avc1.${hex(cfg[i + 4])}${hex(cfg[i + 5])}${hex(cfg[i + 6])}`;
      }
    }
  }
  return 'avc1.42E01E';
}

function readPts(view: DataView): number {
  // 64-bit big-endian microseconds; safe as a double for any realistic uptime.
  return Number(view.getBigUint64(1));
}

export default function HmdCastView({ host, serial, profile, stopCast, onClose }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [state, setState] = useState<'connecting' | 'starting' | 'live' | 'ended' | 'error'>('connecting');
  const [note, setNote] = useState<string>('');
  const [dims, setDims] = useState<{ w: number; h: number }>({ w: 640, h: 640 });
  const [clientStats, setClientStats] = useState<{ fps: number; kbps: number; queue: number }>({ fps: 0, kbps: 0, queue: 0 });
  const [hmdStats, setHmdStats] = useState<HeadsetStats | null>(null);
  const [since, setSince] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [big, setBig] = useState(false);
  const [wanted, setWanted] = useState<HmdCastProfile>(profile);
  const [generation, setGeneration] = useState(0);

  const supported = typeof (window as any).VideoDecoder === 'function';

  useEffect(() => {
    if (!supported) { setState('error'); setNote('Deze weergave heeft WebCodecs nodig (Chrome 94+ / Electron).'); return; }
    let closed = false;
    let decoder: VideoDecoder | null = null;
    let configured = false;
    let needKey = true;
    let frames = 0, bytes = 0;
    const url = `ws://${host}:9090/ws/hmd/${encodeURIComponent(serial)}/cast?q=${wanted}`;
    const ws = new WebSocket(url);
    ws.binaryType = 'arraybuffer';
    setState('connecting'); setNote(''); setHmdStats(null); setSince(null);

    const canvas = canvasRef.current;
    const ctx2d = canvas?.getContext('2d');

    const resetDecoder = () => {
      try { decoder?.close(); } catch { /* already closed */ }
      decoder = null; configured = false; needKey = true;
    };
    const askKeyframe = () => { try { ws.send(JSON.stringify({ type: 'keyframe' })); } catch { /* socket gone */ } };

    const makeDecoder = (codec: string) => {
      const d = new VideoDecoder({
        output: (frame) => {
          if (closed || !ctx2d || !canvas) { frame.close(); return; }
          if (canvas.width !== frame.displayWidth || canvas.height !== frame.displayHeight) {
            canvas.width = frame.displayWidth; canvas.height = frame.displayHeight;
            setDims({ w: frame.displayWidth, h: frame.displayHeight });
          }
          ctx2d.drawImage(frame, 0, 0);
          frame.close();
        },
        error: (e) => {
          console.warn('[hmd-cast] decoder error', e);
          resetDecoder();
          askKeyframe();
        },
      });
      d.configure({ codec, optimizeForLatency: true });
      return d;
    };

    ws.onopen = () => setState('starting');
    ws.onmessage = (ev) => {
      if (closed) return;
      if (typeof ev.data === 'string') {
        let m: any;
        try { m = JSON.parse(ev.data); } catch { return; }
        if (m.type === 'hello') {
          setSince(m.since ? new Date(m.since).getTime() : Date.now());
          if (m.width && m.height) setDims({ w: m.width, h: m.height });
        } else if (m.type === 'stats') {
          setHmdStats(m);
        } else if (m.type === 'ended') {
          setState('ended'); setNote(endedText(m.reason));
        } else if (m.type === 'error') {
          setState('error'); setNote(m.error || 'onbekende fout');
        }
        return;
      }
      const buf = ev.data as ArrayBuffer;
      if (buf.byteLength <= HEADER) return;
      const flags = new Uint8Array(buf, 0, 1)[0];
      const payload = new Uint8Array(buf, HEADER);
      if (flags & FLAG_CONFIG) {
        const codec = codecStringFromConfig(payload);
        resetDecoder();
        try { decoder = makeDecoder(codec); configured = true; } catch (e) {
          setState('error'); setNote(`Decoder (${codec}) niet beschikbaar: ${e}`);
        }
        return;
      }
      const key = (flags & FLAG_KEY) !== 0;
      if (!configured) {
        // Keyframes carry SPS/PPS, so a decoder can start from one.
        if (!key) return;
        try { decoder = makeDecoder(codecStringFromConfig(payload)); configured = true; } catch (e) {
          setState('error'); setNote(`Decoder niet beschikbaar: ${e}`); return;
        }
      }
      if (needKey && !key) return;
      if (!decoder || decoder.state !== 'configured') return;
      // Latency guard: a decoder that is falling behind drops deltas until the next keyframe.
      if (!key && decoder.decodeQueueSize > 8) { needKey = true; return; }
      needKey = false;
      frames++; bytes += buf.byteLength;
      try {
        decoder.decode(new EncodedVideoChunk({
          type: key ? 'key' : 'delta',
          timestamp: readPts(new DataView(buf)),
          data: payload,
        }));
        setState((s) => (s === 'live' ? s : 'live'));
      } catch (e) {
        console.warn('[hmd-cast] decode', e);
        resetDecoder();
        askKeyframe();
      }
    };
    ws.onerror = () => { if (!closed) { setState('error'); setNote('Geen verbinding met de agent'); } };
    ws.onclose = () => { if (!closed) setState((s) => (s === 'ended' || s === 'error' ? s : 'ended')); };

    const tick = setInterval(() => {
      setClientStats({ fps: frames, kbps: Math.round(bytes * 8 / 1000), queue: decoder?.decodeQueueSize ?? 0 });
      frames = 0; bytes = 0;
    }, 1000);

    return () => {
      closed = true;
      clearInterval(tick);
      try { ws.close(); } catch { /* ignore */ }
      resetDecoder();
    };
  }, [host, serial, wanted, generation, supported]);

  useEffect(() => {
    if (since == null) return;
    const t = setInterval(() => setElapsed(Math.max(0, Math.round((Date.now() - since) / 1000))), 1000);
    return () => clearInterval(t);
  }, [since]);

  useEffect(() => {
    if (!big) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setBig(false); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [big]);

  const switchProfile = async (p: HmdCastProfile) => {
    if (p === wanted) return;
    // The session's profile is fixed by the first viewer: end it, then rejoin with the new one.
    setWanted(p);
    if (stopCast) { try { await stopCast(); } catch { /* the reconnect below still works */ } }
    setGeneration((g) => g + 1);
  };

  const stateText = state === 'connecting' ? 'Verbinden met de agent…'
    : state === 'starting' ? 'Headset start de weergave…'
    : state === 'live' ? `Live · ${fmtElapsed(elapsed)}`
    : state === 'ended' ? `Gestopt${note ? ' · ' + note : ''}`
    : `Fout · ${note}`;
  const stateColor = state === 'live' ? '#1e8e3e' : state === 'error' ? '#c0392b' : '#54626f';

  // Fullscreen fills the screen; the small view fills its column. The DOM tree
  // is identical in both modes on purpose: the decoder holds the 2D context of
  // this canvas element, so React must reuse the node rather than remount it.
  //
  // In fullscreen the canvas is stretched to the whole area and letterboxed
  // with object-fit. Sizing it with width/height auto (as this did first) caps
  // it at the bitmap's own 640 px, which is why it stayed small on a 4K screen;
  // an explicit height plus aspect-ratio would instead distort it on a wide
  // screen. object-fit: contain is the one that scales up and keeps the shape.
  const box: CSSProperties = big
    ? { position: 'fixed', inset: 0, background: '#0b1117', zIndex: 1200, display: 'flex', padding: 12 }
    : { marginTop: 14 };
  const innerStyle: CSSProperties = big
    ? { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, gap: 2 }
    : { width: '100%' };
  const canvasWrapStyle: CSSProperties = big
    ? { flex: '1 1 auto', minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }
    : {};
  // Fullscreen size is arithmetic, not a max-constraint: the height is whichever
  // of the two limits binds (screen height minus the control strip, or screen
  // width divided by the aspect), and the width follows from aspect-ratio. So
  // the picture grows past the 640 px bitmap and can never be stretched.
  // object-fit is a belt-and-braces no-op while the box already has the ratio.
  const ratio = dims.w / dims.h;
  const canvasStyle: CSSProperties = big
    ? {
        height: `min(calc(100vh - 92px), calc((100vw - 28px) / ${ratio}))`,
        width: 'auto', maxWidth: '100%', aspectRatio: `${dims.w} / ${dims.h}`,
        objectFit: 'contain', background: '#000', borderRadius: 8, display: 'block',
      }
    : { width: '100%', aspectRatio: `${dims.w} / ${dims.h}`, background: '#000', borderRadius: 8, display: 'block' };
  const smallBtn = (active: boolean): CSSProperties => ({
    padding: '5px 9px', borderRadius: 8, border: '1px solid ' + (active ? '#2B6AC0' : '#c9d1da'), fontSize: 12,
    background: active ? '#2B6AC0' : big ? '#16202e' : '#fff', color: active ? '#fff' : big ? '#dfe6ee' : '#16202e', cursor: 'pointer',
  });
  const textColor = big ? '#dfe6ee' : '#54626f';

  return (
    <div style={box} onClick={(e) => e.stopPropagation()}>
      <div style={innerStyle}>
        <div style={canvasWrapStyle}>
          <canvas ref={canvasRef} width={dims.w} height={dims.h} style={canvasStyle} />
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', marginTop: 6, gap: big ? 16 : 8, flexWrap: 'wrap',
          // Centred under the centred picture in fullscreen; the panel keeps
          // the state on the left and the buttons on the right.
          justifyContent: big ? 'center' : 'space-between',
        }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: state === 'live' && big ? '#6fdc8c' : stateColor }}>{stateText}</span>
          <span style={{ display: 'flex', gap: 4 }}>
            {HMD_CAST_PROFILES.map((p) => (
              <button key={p.id} style={smallBtn(p.id === wanted)} title={p.hint} onClick={() => switchProfile(p.id)}>{p.label}</button>
            ))}
            <button style={smallBtn(false)} title={big ? 'Terug naar het paneel (Esc)' : 'Schermvullend'} onClick={() => setBig((b) => !b)}>{big ? 'Klein' : 'Groot'}</button>
            {(state === 'ended' || state === 'error') && (
              <button style={smallBtn(false)} onClick={() => setGeneration((g) => g + 1)}>Opnieuw</button>
            )}
            <button style={smallBtn(false)} onClick={onClose}>Stop</button>
          </span>
        </div>
        <div style={{ fontSize: 11, color: textColor, marginTop: 4, lineHeight: 1.5, textAlign: big ? 'center' : 'left' }}>
          Ontvangen {clientStats.fps} fps · {fmtKbps(clientStats.kbps)}{clientStats.queue > 2 ? ` · wachtrij ${clientStats.queue}` : ''}
          {hmdStats && (
            <>
              {' · '}Headset: {hmdStats.fps ?? '–'} fps · {fmtKbps(hmdStats.kbps ?? 0)} · encoder {hmdStats.enc_ms ?? '–'} ms
              {hmdStats.cpu_pct != null ? ` · CPU ${hmdStats.cpu_pct}%` : ''}
              {hmdStats.batt_temp_c != null ? ` · ${hmdStats.batt_temp_c}°C` : ''}
              {hmdStats.batt_ma != null ? ` · ${hmdStats.batt_ma} mA` : ''}
            </>
          )}
        </div>
        {state === 'live' && !big && (
          <div style={{ fontSize: 11, color: textColor, marginTop: 2 }}>
            De headset codeert dit zelf; de weergave stopt vanzelf zodra dit venster dicht is en uiterlijk na 15 minuten.
          </div>
        )}
      </div>
    </div>
  );
}

function endedText(reason?: string): string {
  switch (reason) {
    case 'last viewer left': return 'laatste kijker weg';
    case 'max duration': return 'maximale duur (15 min) bereikt';
    case 'stopped by operator': return 'gestopt door operator';
    case 'uplink closed': return 'headset stopte de weergave';
    default: return reason || '';
  }
}

function fmtKbps(kbps: number): string {
  return kbps >= 1000 ? `${(kbps / 1000).toFixed(1)} Mbit/s` : `${kbps} kbit/s`;
}

function fmtElapsed(s: number): string {
  const m = Math.floor(s / 60), r = s % 60;
  return m > 0 ? `${m}:${String(r).padStart(2, '0')}` : `${r} s`;
}
